import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useCoffee } from "@/context/CoffeeContext";
import { PAGE, CARD, INPUT } from "@/lib/styles";
import { Shield, Users, FileText, Network, Table2, File, CalendarCheck, Share2, Coffee, Search, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STAT_CARDS = [
  { key: "users", label: "Usuarios", Icon: Users, color: "text-indigo-500 bg-indigo-500/10" },
  { key: "notes", label: "Notas", Icon: FileText, color: "text-sky-500 bg-sky-500/10" },
  { key: "mindmaps", label: "Mapas", Icon: Network, color: "text-violet-500 bg-violet-500/10" },
  { key: "sheets", label: "Hojas", Icon: Table2, color: "text-emerald-500 bg-emerald-500/10" },
  { key: "documents", label: "Documentos", Icon: File, color: "text-rose-500 bg-rose-500/10" },
  { key: "tasks", label: "Tareas", Icon: CalendarCheck, color: "text-amber-500 bg-amber-500/10" },
  { key: "shares", label: "Enlaces compartidos", Icon: Share2, color: "text-cyan-500 bg-cyan-500/10" },
  { key: "trashed", label: "En papelera", Icon: Trash2, color: "text-slate-500 bg-slate-500/10" },
  { key: "coffees", label: "Cafés (ml) activos", Icon: Coffee, color: "text-orange-500 bg-orange-500/10" },
];

const fmtDate = (s) => {
  if (!s) return "-";
  try { return format(parseISO(s), "d 'de' MMMM yyyy", { locale: es }); } catch { return "-"; }
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const { status } = useCoffee();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const loadUsers = useCallback(async (search = "") => {
    try {
      const { data } = await api.get("/admin/users", { params: { q: search } });
      setUsers(data.users);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (err) {
        if (err.response?.status === 403) { setDenied(true); setLoading(false); return; }
        toast.error(formatApiError(err.response?.data?.detail));
      }
      await loadUsers("");
      setLoading(false);
    })();
  }, [loadUsers]);

  useEffect(() => {
    const t = setTimeout(() => { if (!denied) loadUsers(q); }, 300);
    return () => clearTimeout(t);
  }, [q, denied, loadUsers]);

  if (denied) {
    return (
      <div className={`${PAGE} font-body`}>
        <Navbar />
        <div className="max-w-md mx-auto p-10 text-center mt-16">
          <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto mb-4"><Shield size={26} /></div>
          <h1 className="font-heading font-normal text-2xl mb-2">Acceso restringido</h1>
          <p className="font-body text-sm text-slate-500 dark:text-slate-400" data-testid="admin-denied">Esta sección es solo para administradores.</p>
          <button onClick={() => navigate("/")} className="mt-5 text-indigo-500 hover:text-indigo-400 text-sm font-medium">Volver al inicio</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="admin-panel">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-indigo-600/10 text-indigo-500 flex items-center justify-center"><Shield size={26} /></div>
          <div>
            <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight">Administración</h1>
            <p className="font-mono2 text-xs text-slate-500 dark:text-slate-400 mt-1">Panel de control de Notacora {status?.is_admin ? "· sesión de administrador" : ""}</p>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
              {STAT_CARDS.map(({ key, label, Icon, color }) => (
                <div key={key} className={`${CARD} p-5`} data-testid={`stat-${key}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${color}`}><Icon size={20} /></div>
                  <p className="font-heading font-light text-3xl tracking-tight">{stats?.[key] ?? 0}</p>
                  <p className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400 mt-1">{label}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="font-heading font-medium text-lg flex items-center gap-2"><Users size={18} className="text-indigo-500" /> Usuarios <span className="text-slate-400 font-normal">({users.length})</span></h2>
              <div className="relative w-full max-w-xs">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={`${INPUT} pl-10 py-2`} placeholder="Buscar por email o nombre..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search" />
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl" data-testid="admin-users-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-900 text-left font-mono2 text-[11px] text-slate-400 uppercase tracking-wider">
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3">Rol</th>
                    <th className="px-4 py-3 text-center">Elementos</th>
                    <th className="px-4 py-3 text-center">Café (ml)</th>
                    <th className="px-4 py-3">Registrado</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 font-body">No se encontraron usuarios.</td></tr>
                  ) : users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-body font-medium">{u.name || u.email.split("@")[0]}</p>
                        <p className="font-mono2 text-[11px] text-slate-400">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {u.is_admin ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500 border border-indigo-500/20"><Shield size={11} /> Admin</span>
                        ) : (
                          <span className="text-[11px] text-slate-400 font-mono2">Usuario</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center font-mono2 text-slate-500 dark:text-slate-300">{u.notes_count}</td>
                      <td className="px-4 py-3 text-center font-mono2 text-slate-500 dark:text-slate-300">{u.coffee_balance != null ? u.coffee_balance : "-"}</td>
                      <td className="px-4 py-3 font-mono2 text-[12px] text-slate-400">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
